
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * OrderMarginNotify struct
 */
class OrderMarginNotify {
  /**
   * Initialize struct
   * @param {!number=} buyMargin
   * @param {!number=} sellMargin
   * @param {!number=} buyMarginHedged
   * @param {!number=} sellMarginHedged
   * @constructor
   */
  constructor (buyMargin = 0.0, sellMargin = 0.0, buyMarginHedged = 0.0, sellMarginHedged = 0.0) {
    this.BuyMargin = buyMargin
    this.SellMargin = sellMargin
    this.BuyMarginHedged = buyMarginHedged
    this.SellMarginHedged = sellMarginHedged
  }

  /**
   * Copy struct (shallow copy)
   * @this {!OrderMarginNotify}
   * @param {!OrderMarginNotify} other Other struct
   * @returns {!OrderMarginNotify} This struct
   */
  copy (other) {
    if (other.BuyMargin != null) {
      this.BuyMargin = other.BuyMargin
    } else {
      this.BuyMargin = null
    }
    if (other.SellMargin != null) {
      this.SellMargin = other.SellMargin
    } else {
      this.SellMargin = null
    }
    if (other.BuyMarginHedged != null) {
      this.BuyMarginHedged = other.BuyMarginHedged
    } else {
      this.BuyMarginHedged = null
    }
    if (other.SellMarginHedged != null) {
      this.SellMarginHedged = other.SellMarginHedged
    } else {
      this.SellMarginHedged = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!OrderMarginNotify}
   * @returns {!OrderMarginNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new OrderMarginNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new OrderMarginNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!OrderMarginNotify}
   * @param {!OrderMarginNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof OrderMarginNotify)) {
      throw new TypeError('Instance of OrderMarginNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!OrderMarginNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      BuyMargin: ((this.BuyMargin != null) ? this.BuyMargin : null),
      SellMargin: ((this.SellMargin != null) ? this.SellMargin : null),
      BuyMarginHedged: ((this.BuyMarginHedged != null) ? this.BuyMarginHedged : null),
      SellMarginHedged: ((this.SellMarginHedged != null) ? this.SellMarginHedged : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return OrderMarginNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!OrderMarginNotify} other Object value
   * @returns {!OrderMarginNotify} Created struct
   */
  static fromObject (other) {
    return new OrderMarginNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!OrderMarginNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return OrderMarginNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!OrderMarginNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5140
  }
}

export { OrderMarginNotify };

/**
 * Fast Binary Encoding OrderMarginNotify field model
 */
class FieldModelOrderMarginNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.BuyMargin = new fbe.FieldModelDouble(buffer, 4 + 4)
    this.SellMargin = new fbe.FieldModelDouble(buffer, this.BuyMargin.fbeOffset + this.BuyMargin.fbeSize)
    this.BuyMarginHedged = new fbe.FieldModelDouble(buffer, this.SellMargin.fbeOffset + this.SellMargin.fbeSize)
    this.SellMarginHedged = new fbe.FieldModelDouble(buffer, this.BuyMarginHedged.fbeOffset + this.BuyMarginHedged.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.BuyMargin.fbeSize + this.SellMargin.fbeSize + this.BuyMarginHedged.fbeSize + this.SellMarginHedged.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.BuyMargin.fbeExtra + this.SellMargin.fbeExtra + this.BuyMarginHedged.fbeExtra + this.SellMarginHedged.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelOrderMarginNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5140
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelOrderMarginNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelOrderMarginNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.BuyMargin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.BuyMargin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BuyMargin.fbeSize

    if ((fbeCurrentSize + this.SellMargin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SellMargin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SellMargin.fbeSize

    if ((fbeCurrentSize + this.BuyMarginHedged.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.BuyMarginHedged.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BuyMarginHedged.fbeSize

    if ((fbeCurrentSize + this.SellMarginHedged.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SellMarginHedged.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SellMarginHedged.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelOrderMarginNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelOrderMarginNotify}
   * @param {!OrderMarginNotify} fbeValue Default value, defaults is new OrderMarginNotify()
   * @returns {!OrderMarginNotify} OrderMarginNotify value
   */
  get (fbeValue = new OrderMarginNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelOrderMarginNotify}
   * @param {!OrderMarginNotify} fbeValue OrderMarginNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.BuyMargin.fbeSize) <= fbeStructSize) {
      fbeValue.BuyMargin = this.BuyMargin.get()
    } else {
      fbeValue.BuyMargin = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BuyMargin.fbeSize

    if ((fbeCurrentSize + this.SellMargin.fbeSize) <= fbeStructSize) {
      fbeValue.SellMargin = this.SellMargin.get()
    } else {
      fbeValue.SellMargin = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SellMargin.fbeSize

    if ((fbeCurrentSize + this.BuyMarginHedged.fbeSize) <= fbeStructSize) {
      fbeValue.BuyMarginHedged = this.BuyMarginHedged.get()
    } else {
      fbeValue.BuyMarginHedged = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BuyMarginHedged.fbeSize

    if ((fbeCurrentSize + this.SellMarginHedged.fbeSize) <= fbeStructSize) {
      fbeValue.SellMarginHedged = this.SellMarginHedged.get()
    } else {
      fbeValue.SellMarginHedged = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SellMarginHedged.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelOrderMarginNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelOrderMarginNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelOrderMarginNotify}
   * @param {!OrderMarginNotify} fbeValue OrderMarginNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelOrderMarginNotify}
   * @param {OrderMarginNotify} fbeValue OrderMarginNotify value
   */
  setFields (fbeValue) {
    this.BuyMargin.set(fbeValue.BuyMargin)
    this.SellMargin.set(fbeValue.SellMargin)
    this.BuyMarginHedged.set(fbeValue.BuyMarginHedged)
    this.SellMarginHedged.set(fbeValue.SellMarginHedged)
  }
}

export { FieldModelOrderMarginNotify };

/**
 * Fast Binary Encoding OrderMarginNotify model
 */
class OrderMarginNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelOrderMarginNotify(this.buffer, 4)
  }

  /**
   * Get the OrderMarginNotify model
   * @this {!OrderMarginNotifyModel}
   * @returns {!FieldModelOrderMarginNotify} model OrderMarginNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!OrderMarginNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!OrderMarginNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return OrderMarginNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!OrderMarginNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelOrderMarginNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!OrderMarginNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!OrderMarginNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!OrderMarginNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!OrderMarginNotifyModel}
   * @param {!OrderMarginNotify} value OrderMarginNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!OrderMarginNotifyModel}
   * @param {!OrderMarginNotify} value OrderMarginNotify value, defaults is new OrderMarginNotify()
   * @return {!object} Deserialized OrderMarginNotify value and its size
   */
  deserialize (value = new OrderMarginNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new OrderMarginNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new OrderMarginNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!OrderMarginNotifyModel}
   * @param {!number} prev Previous OrderMarginNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { OrderMarginNotifyModel };
